import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "../components/PageTitleCompoent";
import "../css/AddPackage.css";
import api from "../api";

export default function AddGroomingPackage() {
  const navigate = useNavigate();
  const servicesRef = useRef(null);

  const [packageName, setPackageName] = useState("");
  const [duration, setDuration] = useState("");
  const [amount, setAmount] = useState("");
  const [subscribedAmount, setSubscribedAmount] = useState("");
  const [promoTag, setPromoTag] = useState("");
  const [services, setServices] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [newService, setNewService] = useState("");

  const tagMapping = {
    Popular: "popular",
    "Great Deals": "great_deals",
    "Steal Deals": "steal_deals",
  };

  // Animate services list on update
  useEffect(() => {
    if (servicesRef.current) {
      gsap.fromTo(
        servicesRef.current.children,
        { x: -30, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.4, stagger: 0.1, ease: "power2.out" }
      );
    }
  }, [services]);

  // Service handlers
  const handleAddServiceClick = () => setShowInput(true);
  const handleCloseInput = () => {
    setShowInput(false);
    setNewService("");
  };
  const handleInputChange = (e) => setNewService(e.target.value);
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && newService.trim()) {
      setServices((prev) => [...prev, newService.trim()]);
      setNewService("");
      setShowInput(false);
      toast.success("Service added!");
    }
  };
  const handleDeleteService = (indexToDelete) => {
    setServices((prev) => prev.filter((_, idx) => idx !== indexToDelete));
    toast.info("Service removed");
  };

  // Save handler
  const handleSave = async () => {
    if (!packageName.trim()) {
      toast.error("Please enter package name");
      return;
    }
    if (!amount.trim() || !subscribedAmount.trim()) {
      toast.error("Please enter both price and subscribed offer price");
      return;
    }

    const newPackage = {
      package_name: packageName.trim(),
      duration: duration.trim(),
      package_amount: amount.trim(),
      subscribed_user_offer_amount: subscribedAmount.trim(),
      services: services.join(", "),
      tags: tagMapping[promoTag] || "",
    };

    try {
      const res = await api.post("/grooming-packages/add/", newPackage);
      toast.success("Package saved successfully!");
      navigate(-1, { state: { newPackage: res.data } });
    } catch (error) {
      console.error("Error saving grooming package:", error);
      toast.error("Failed to save package. Please check inputs.");
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    hover: { scale: 1.03, transition: { type: "spring", stiffness: 300 } },
  };

  return (
    <div className="Add-package-container">
      <Sidebar />
      <motion.div className="Add-package-main" initial="hidden" animate="visible" variants={fadeInUp}>
        <Header />
        <div className="Add-package-content">
          <PageTitle title="Add Package" onBack={() => navigate(-1)} />

          <motion.div className="Add-package-grid" initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { duration: 0.6 } }}>
            {/* Package Name & Duration */}
            <div className="Add-package-form-row">
              <motion.div className="Add-package-form-group1" whileHover={{ scale: 1.02 }}>
                <label><h3 className="Add-package-text2">Package Name</h3></label>
                <input
                  className="Add-package-inputbox"
                  type="text"
                  value={packageName}
                  onChange={(e) => setPackageName(e.target.value)}
                  placeholder="Enter Package Name..."
                />
              </motion.div>

              <motion.div className="Add-package-form-group1" whileHover={{ scale: 1.02 }}>
                <label><h3 className="Add-package-text2">Duration</h3></label>
                <input
                  className="Add-package-inputbox"
                  type="text"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="Enter Duration..."
                />
              </motion.div>
            </div>

            {/* Amounts */}
            <div className="Add-package-form-row">
              <motion.div className="Add-package-form-group1" whileHover={{ scale: 1.02 }}>
                <label><h3 className="Add-package-text2">Package Amount</h3></label>
                <input
                  className="Add-package-inputbox"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter Amount..."
                />
              </motion.div>

              <motion.div className="Add-package-form-group1" whileHover={{ scale: 1.02 }}>
                <label><h3 className="Add-package-text2">Subscribed User Offer Amount</h3></label>
                <input
                  className="Add-package-inputbox"
                  type="number"
                  value={subscribedAmount}
                  onChange={(e) => setSubscribedAmount(e.target.value)}
                  placeholder="Enter Offer Amount..."
                />
              </motion.div>
            </div>

            {/* Tags */}
            <div className="Add-package-form-group1">
              <label><h3 className="Add-package-text2">Tags</h3></label>
              <div className="Add-package-tags-buttons">
                {["Popular", "Great Deals", "Steal Deals"].map((tagOption) => (
                  <motion.button
                    key={tagOption}
                    className={`Add-package-tag-button ${
                      promoTag === tagOption ? `${tagMapping[tagOption]}_selected` : ""
                    }`}
                    onClick={() => setPromoTag(tagOption)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {tagOption}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="Add-package-form-group1">
              <label><h3 className="Add-package-text2">Services</h3></label>
              <div className="Add-package-service-box">
                <ul className="service-list" ref={servicesRef}>
                  <AnimatePresence>
                    {services.map((service, index) => (
                      <motion.li
                        key={index}
                        className="service-item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20, transition: { duration: 0.3 } }}
                        whileHover={{ scale: 1.05 }}
                      >
                        <span className="service-hyphen" onClick={() => handleDeleteService(index)}>−</span>
                        {service}
                      </motion.li>
                    ))}
                  </AnimatePresence>
                </ul>
                {showInput ? (
                  <input
                    type="text"
                    value={newService}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    onBlur={handleCloseInput}
                    autoFocus
                    className="service-input"
                  />
                ) : (
                  <motion.button
                    className="add-service-btn"
                    onClick={handleAddServiceClick}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    +
                  </motion.button>
                )}
              </div>
            </div>

            {/* Save Button */}
            <motion.div className="Add-package-button-wrapper" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.95 }}>
              <button className="Add-package-save-button" onClick={handleSave} type="button">
                Save
              </button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Toast Container */}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
    </div>
  );
}
